import {ControlMode} from '.'
import {Vector2} from 'three'
import type Controls from '../controls'

type MouseEventHandlers = {
  onUse?: () => void
  onRelease?: () => void
}

// This mode handles controlling the rotation with the mouse as well
// as the zoom with the mousewheel. Since these controls should take
// priority, the methods `onUse` and `onRelease` are called on instances
// of this class when a user takes control.
class MouseMode extends ControlMode {
  onUse: () => void
  onRelease: () => void

  translating = false
  shift = false

  drag = {
    sensitivity: 0.4,
    start: new Vector2(),
    end: new Vector2(),
  }

  // Control mode initialization
  constructor(controls: Controls, active?: boolean, eventHandlers?: MouseEventHandlers) {
    super(controls, active)

    const {onUse, onRelease} = eventHandlers ?? {}

    this.onUse =
      onUse ??
      function () {
        return void 0
      }
    this.onRelease =
      onRelease ??
      function () {
        return void 0
      }
  }

  init() {
    const controlsEl = this.controls.containerEl()

    controlsEl?.addEventListener('mousewheel', (event: WheelEvent) => {
      this.onScroll(event)
    })
    controlsEl?.addEventListener('DOMMouseScroll', (event: WheelEvent) => {
      this.onScroll(event)
    })
    controlsEl?.addEventListener('mousedown', e => {
      this.mouseDown(e)
    })
    controlsEl?.addEventListener('contextmenu', event => event.preventDefault())
  }

  mouseDown = (event: MouseEvent) => {
    if (!this.active) {
      this.onUse?.()
    }

    event.preventDefault()
    this.bindHandlers(true)
    this.drag.start.set(event.clientX, event.clientY)

    // if we right clicked, lets translate
    if (event.button === 2) {
      this.translating = true
    }
  }

  onScroll = (e: Event & WheelEvent) => {
    if (!this.active) {
      this.onUse?.()
    }
    e.stopPropagation()
    e.preventDefault()

    // As in: https://github.com/tbuser/thingiview.js/blob/master/javascripts/thingiview.js#L193
    /**
     * The previous value used here, wheelDelta, has been deprecated.
     * Furthermore, the value of deltaY is reversed from what it used to be, i.e.
     * scrolling up now returns a negative value.
     * To account for this, we flip the sign of the value, adding 1 to ensure the new value
     * matches the old one.
     */
    const delta = ~e.deltaY + 1 || -40 * e.detail
    if (delta > 0) {
      this.controls.changeZoom(+0.1)
    } else {
      this.controls.changeZoom(-0.1)
    }
    if (this.active) {
      this.onRelease()
    }
  }

  // Secondary handlers
  bindHandlers(engage = true) {
    const action = engage ? 'addEventListener' : 'removeEventListener'
    const controlsEl = this.controls.containerEl()
    if (controlsEl) {
      controlsEl[action]('mousemove', this.mouseMove as EventListener)
      controlsEl[action]('mouseup', this.mouseUp)
    }
  }

  mouseMove = (event: MouseEvent) => {
    if (this.translating) {
      this.mouseTranslate(event)
    }
    this.mouseOrbit(event)
  }

  mouseUp = () => {
    if (this.active) {
      this.onRelease?.()
    }
    this.bindHandlers(false)
    // If we let go of the mouse and shift isn't held down
    // then translation time is over
    if (this.translating && !this.shift) {
      this.translating = false
    }
  }

  // Movement helpers
  mouseTranslate = (event: MouseEvent) => {
    const drag = this.drag.end
      .set(event.clientX, event.clientY)
      .clone()
      .sub(this.drag.start)
      .multiplyScalar(this.drag.sensitivity)
    this.drag.start.copy(this.drag.end)

    this.controls.translateUp(drag.y)
    this.controls.translateRight(drag.x)
  }

  mouseOrbit = (event: MouseEvent) => {
    const drag = this.drag.end
      .set(event.clientX, event.clientY)
      .clone()
      .sub(this.drag.start)
      .multiplyScalar(this.drag.sensitivity)
    this.drag.start.copy(this.drag.end)

    this.controls.orbitUp(this.controls.orbitIncrement * drag.y)
    this.controls.orbitRight(this.controls.orbitIncrement * -drag.x)
  }
}

export default MouseMode
